import React, { useEffect, useState } from 'react';
import { useToggle } from 'react-use-v13';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Label, Button } from 'design-system/Form';
import { Checkbox } from 'design-system';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import useFetchBatches from 'react-scale/containers/corp/backfills/batch/hooks/useFetchBatches';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import SelectProject from 'corp/components/UI/SelectProject';
import Select from 'corp/components/UI/Select';
import { IUserJson } from '@scale/shared/types/user';
import { invokeDynamicCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import {
  IDynamicCorpJobResponse,
  formatDynamicCorpJobResponse,
} from 'corp/views/RoadrunnerUtilsPage';
import { v4 as uuidv4 } from 'uuid';

const description = `
### Description:
- This tool allows you to update and regenerate a deliverable CSV for a previous delivery
- Select the project and batch, and then click the button to update.

NOTE: results of any run can be previewed on google sheets here: https://docs.google.com/spreadsheets/d/1NNli0Of9Uw5ex2sd_-XDvOrfq4C-itvRAmekHBIz5mk/edit#gid=1425647214

##### Author: Ahmad Elshenawy
---
`;

interface SelectOption {
  label: string;
  value: any;
}

const projectIdentifiers: string[] = ['General', 'SFV', 'Comedy'];

const UpdateDelivery: React.FC<{ user: IUserJson }> = ({ user }) => {
  const [project, setProject] = useState<SelectOption | null>();
  const [projectIdentifier, setProjectIdentifier] = useState<SelectOption | null>();
  const [batch, setBatch] = useState<SelectOption | null>();
  const [dryRun, toggleDryRun] = useToggle(true);
  const [query, setQuery] = useState<any>({});
  const { batches, error, isLoading } = useFetchBatches(query);
  const [batchOptions, setBatchOptions] = useState<SelectOption[] | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const [updateResult, setUpdateResult] = useState<string>();
  const [runUuid, setUuid] = useState<string>();
  const enabled = project && batch && projectIdentifier && !message;

  useEffect(() => {
    if (project) {
      setQuery({ project: project.value });
      setBatch(null);
      setBatchOptions(null);
      setMessage(undefined);
      setUpdateResult(undefined);
    }
  }, [project]);

  useEffect(() => {
    setBatchOptions(
      batches
        .filter(i => i.name.endsWith('_delivery'))
        .map(batch => {
          return { label: batch.name, value: batch._id };
        }),
    );
  }, [batches]);

  const handleProject = async (project: SelectOption) => {
    setProject(project);
  };

  const handleProjectIdentifier = async (projectIdentifier: SelectOption) => {
    setProjectIdentifier(projectIdentifier);
  };

  const handleBatch = async (batch: SelectOption) => {
    setBatch(batch);
    setMessage(undefined);
  };

  const handleCheckBatch = async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await apiFetch(null, 'corp-api/roadrunner-utils/batch-task-count', {
        method: 'PUT',
        body: { batchId: batch?.value, projectId: project?.value },
      });
      setLoading(false);
      setMessage(res.message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setMessage('');
    const newRunUUID = uuidv4();
    setUuid(newRunUUID);

    let taskIds: string = '';
    let currentResponse: IDynamicCorpJobResponse;
    try {
      const res = await apiFetch(null, 'corp-api/roadrunner-utils/batch-tasks', {
        method: 'PUT',
        body: { batchId: batch?.value, projectId: project?.value },
      });
      taskIds = res.tasks.join(' ');
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      currentResponse = {
        message: `Internal server error -- Unable to fetch tasks\n${error}`,
        success: false,
      };
    }

    const generateDeliveryArgs = {
      projectDescription: projectIdentifier!.value,
      taskIdsString: taskIds,
      edgeCaseTaskIdsString: '',
      dryRun,
      allowRedelivery: true,
      user,
      deliveryBatchName: batch?.label,
      inputProjectId: project?.value,
      runUuid: newRunUUID,
    };

    try {
      const job = await invokeDynamicCorpJob(
        'flamingo-generate-delivery',
        generateDeliveryArgs,
        true,
      );

      if (job.status === 'errored') {
        currentResponse = {
          message: job.errorReason || 'Internal server error',
          success: false,
        };
      } else {
        const results = job.jsonOutput;
        currentResponse = {
          message: results?.message || `Successful!`,
          success: true,
          corpJobOrSfnId: results?.stepFunctionId || job.id,
        };
      }

      setLoading(false);
      setUpdateResult(formatDynamicCorpJobResponse(currentResponse));
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    }
  };

  const selectBatch = () => {
    if (!project) return 'Select project';
    if (project && error) return `Error fetching batches, please try again later... (${error}}`;
    if (project && isLoading) return 'Loading batches...';
    return (
      <Select value={batch} disabled={!project} onChange={handleBatch} options={batchOptions} />
    );
  };

  const buttonText = () => {
    if (!project) return 'Select project';
    if (!batch) return 'Select batch';
    if (!projectIdentifier) return 'Select project type';
    return 'Check delivery';
  };

  const renderOutput = (text: string) => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={text || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section className="two-columns">
        <section>
          <Label>Project:</Label>
          <SelectProject value={project} onChange={handleProject} />
        </section>
        <section className={project ? '' : 'disabled'}>
          <Label>Batch:</Label>
          {selectBatch()}
        </section>
      </section>
      <section className="two-columns">
        <section className={enabled ? '' : 'disabled'}>
          <Label>Project Type:</Label>
          <Select
            className={project && batch ? '' : 'disabled'}
            value={projectIdentifier}
            onChange={handleProjectIdentifier}
            style={{ width: '50%' }}
            options={projectIdentifiers.map((i: string) => {
              return { label: i, value: i };
            })}
          />
        </section>
      </section>
      <br />
      <section>
        <Checkbox
          checked={dryRun}
          label="Dry Run"
          onChange={_value => {
            toggleDryRun(!dryRun);
          }}
        />
        <hr />
        <Button primary disabled={!enabled || loading} onClick={handleCheckBatch}>
          {buttonText()}
        </Button>
        {message && renderOutput(message)}

        <br />
        {message && (
          <Button primary disabled={!message || loading} requireConfirmation onClick={handleUpdate}>
            {'Update Delivery'}
          </Button>
        )}

        {updateResult && (
          <div>
            {renderOutput(updateResult)}
            <br />
            <h4>Run UUID: {runUuid}</h4>
          </div>
        )}
      </section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default UpdateDelivery;
